import React, {useState, useRef, useEffect} from 'react'
import {Dropdown, Form, InputGroup, Spinner} from 'react-bootstrap'
import {Search, X} from 'react-bootstrap-icons'
import {SearchBarProps} from './types'
import {KTSVG} from '../../../../../_metronic/helpers'
import {useI18n} from '../../../../Utils/_helpers/useI18n'
import {TagModal} from './TagModal'
import {TagOption} from './TagModal'
import {CONTACT_FILTER_OPTIONS} from '../type'

export const SearchBar: React.FC<SearchBarProps> = ({
  searchTerm,
  onSearchChange,
  onClear,
  isLoading,
  placeholder,
  selectedContacts,
  onAddContact,
  onDeleteContacts,
  onAddTags,
  fetchContacts,
  canAddContact = true,
  verifiedSelectedCount = 0,
  onExport,
  isExporting = false,
  filterValue = 'all',
  onFilterChange,
}) => {
  const {t} = useI18n()
  const [showTagModal, setShowTagModal] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleAddTags = () => {
    setShowTagModal(true)
  }

  const handleSaveTags = (selectedTags: TagOption[]) => {
    if (onAddTags) {
      onAddTags(selectedTags).then(() => {
        setShowTagModal(false)
        // Refresh data will be handled by useContactTags hook
      })
    }
  }

  const handleFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (onFilterChange) {
      onFilterChange(event.target.value)
    }
  }

  const hasSelectedContacts = selectedContacts && selectedContacts.length > 0
  const disableAddButton = !hasSelectedContacts || !canAddContact
  const disableExport = isExporting

  return (
    <div className='d-flex flex-column flex-md-row justify-content-between'>
      <div className='d-flex flex-column flex-md-row gap-3 mb-3 mb-md-4'>
        <InputGroup className='search-container'>
          <Form.Control
            placeholder={placeholder}
            value={searchTerm}
            onChange={onSearchChange}
            className='border-end-0'
            disabled={isLoading}
          />
          <InputGroup.Text className='bg-white border-start-0'>
            {isLoading ? (
              <Spinner animation='border' size='sm' />
            ) : searchTerm ? (
              <X className='cursor-pointer search-clear-cursor' onClick={onClear} />
            ) : (
              <Search />
            )}
          </InputGroup.Text>
        </InputGroup>

        <Form.Select
          value={filterValue}
          onChange={handleFilterChange}
          className='w-auto'
          disabled={isLoading}
        >
          {CONTACT_FILTER_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t(opt.labelKey)}
            </option>
          ))}
        </Form.Select>
      </div>

      <div className='d-flex md:justify-content-center justify-content-md-end gap-2 mb-3 mb-md-4'>
        <div className='me-0' ref={dropdownRef}>
          <Dropdown show={showDropdown} onToggle={setShowDropdown}>
            <Dropdown.Toggle className='btn btn-light-primary my-2 m-sm-0 ms-sm-3'>
              {t('RAW_CONTACT_COLLECTION.ACTION')}
              <KTSVG path='/media/icons/duotune/arrows/arr072.svg' className='svg-icon-6 ms-3' />
            </Dropdown.Toggle>

            <Dropdown.Menu className='w-150px'>
              <Dropdown.Item
                className='menu-content p-3 fs-8 text-uppercase'
                disabled={disableAddButton}
                onClick={onAddContact}
              >
                {t('RAW_CONTACT_COLLECTION.ADD_CONTACT')}
              </Dropdown.Item>
              <Dropdown.Item
                className='menu-content p-3 fs-8 text-uppercase'
                disabled={!hasSelectedContacts}
                onClick={handleAddTags}
              >
                {t('RAW_CONTACT_COLLECTION.ADD_TAGS')}
              </Dropdown.Item>
              <Dropdown.Item
                className='menu-content p-3 fs-8 text-uppercase'
                disabled={disableExport}
                onClick={onExport}
              >
                {t('RAW_CONTACT_COLLECTION.EXPORT')}
              </Dropdown.Item>
              <Dropdown.Item
                className='menu-content p-3 fs-8 text-uppercase text-danger'
                disabled={!hasSelectedContacts}
                onClick={onDeleteContacts}
              >
                {t('RAW_CONTACT_COLLECTION.DELETE_CONTACTS')}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <TagModal
        show={showTagModal}
        onClose={() => setShowTagModal(false)}
        onSave={handleSaveTags}
        contactIds={selectedContacts || []}
        fetchContacts={fetchContacts}
      />
    </div>
  )
}
